<template>
    <div class="send-consultant-email-modal">
        <el-dialog
            v-model="visible"
            :title="
                $trans('profile_dossier.send_email_to_supporting_consultant')
            "
            width="1200px"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            :top="'10px'"
        >
            <form @submit.prevent="submitEmail" enctype="multipart/form-data">
                <div class="consultant-section">
                    <div style="display: flex">
                        <img
                            :src="consultantAvatar"
                            class="rounded-circle"
                            style="width: 70px; height: 70px"
                        />
                        <div style="margin-left: 10px; margin-top: 27px">
                            <strong>{{ consultantName }}</strong>
                        </div>
                    </div>
                    <div class="ml-4">
                        <div>{{ consultantTitle }}</div>
                        <div>{{ consultantMobile }}</div>
                    </div>
                    <el-button
                        type="primary"
                        :disabled="!isFormValid"
                        native-type="submit"
                        class="send-btn"
                    >
                        {{ $trans("profile_dossier.send_email") }}
                    </el-button>
                </div>

                <el-form :model="form" label-position="top">
                    <div class="from-to-section">
                        <el-form-item :label="$trans('profile_dossier.mail_from')">
                            <el-input v-model="form.mail_from" readonly />
                        </el-form-item>

                        <el-form-item :label="$trans('profile_dossier.mail_to')">
                            <el-input v-model="form.mail_to" readonly />
                        </el-form-item>
                    </div>
                    <div class="cc-bcc-section">
                        <el-form-item :label="'cc:'">
                            <el-input class="cc-input" v-model="form.mail_cc" />
                        </el-form-item>

                        <el-form-item :label="'bcc:'">
                            <el-input
                                class="bcc-input"
                                v-model="form.mail_bcc"
                            />
                        </el-form-item>
                    </div>

                    <el-form-item :label="$trans('profile_dossier.mail_subject')">
                        <el-input
                            class="subject-input"
                            v-model="form.subject"
                            required
                        />
                    </el-form-item>

                    <div>
                        <QuillEditor
                            v-model:content="form.mail_content"
                            content-type="html"
                            :style="{ height: '300px' }"
                        />
                    </div>
                </el-form>
            </form>
        </el-dialog>
    </div>
</template>

<script>
import axios from "axios";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

import { QuillEditor } from "@vueup/vue-quill";

export default {
    name: "SendConsultantEmailModal",
    components: {
        QuillEditor,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        consultantEmail: String,
        consultantName: String,
        consultantAvatar: String,
        consultantTitle: String,
        consultantMobile: String,
        senderEmail: String,
        senderId: Number,
        profileId: Number,
        vacancyId: Number,
        apiRoute: String,
    },
    emits: ["update:modelValue"],
    data() {
        return {
            form: {
                mail_from: this.senderEmail,
                mail_to: this.consultantEmail,
                mail_cc: "",
                mail_bcc: "",
                subject: "",
                mail_content: "",
                files: [],
            },
            loading: false,
        };
    },
    computed: {
        visible: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
        isFormValid() {
            return (
                this.form.subject.trim() !== "" &&
                this.form.mail_content.trim() !== ""
            );
        },
    },
    methods: {
        handleFiles(event) {
            this.form.files = Array.from(event.target.files);
        },
        async submitEmail() {
            this.loading = true;
            const token = document.head.querySelector(
                'meta[name="csrf-token"]'
            )?.content;
            try {
                const url = this.apiRoute;
                const formData = new FormData();
                for (const [key, value] of Object.entries(this.form)) {
                    if (key === "files") {
                        value.forEach((file) => {
                            formData.append("files[]", file);
                        });
                    } else {
                        formData.append(key, value);
                    }
                }
                formData.append("sender_id", this.senderId);
                formData.append("profile_id", this.profileId);

                await axios.post(url, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "X-CSRF-TOKEN": token,
                    },
                });

                this.$message.success("Email sent successfully!");
                this.visible = false;
            } catch (error) {
                this.$message.error("Failed to send email.");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
:deep(.el-dialog) {
    background-color: #ededed;
}

.send-consultant-email-modal :deep(.el-input input:read-only) {
    background-color: #b7b7b7;
    font-weight: 600;
    height: 30px;
    width: 328px;
    border: none;
}

.send-consultant-email-modal :deep(.el-textarea__inner) {
    font-weight: 400;
}

.send-consultant-email-modal :deep(.el-form-item__label) {
    color: #464646;
    font-style: italic;
}

.send-consultant-email-modal .el-button[disabled] {
    background-color: #d1d1d1 !important;
    border-color: #d1d1d1 !important;
}

/* Custom subject input style */
.send-consultant-email-modal :deep(.subject-input) {
    width: 100%;
    font-weight: 600;
}

/* Upload file input */
.send-consultant-email-modal input[type="file"] {
    margin-top: 13px;
    display: inline-block;
}

/* Avatar */
.send-consultant-email-modal img.rounded-circle {
    object-fit: cover;
}

/* Mail sections (if needed for CC/BCC) */
.send-consultant-email-modal .mail-section {
    display: flex;
    gap: 20px;
    margin-bottom: 1rem;
}

.send-consultant-email-modal .mail-section .el-form-item {
    flex: 1;
}

.consultant-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    margin-top: 20px;
    background-color: #fff;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 10px;
    padding-bottom: 10px;
    align-items: center;
}

.send-btn {
    min-width: 250px;
    font-size: 18px;
    border-color: #41abc4;
    background: #41abc4;
    border-radius: unset;
    margin-top: -233px;
    margin-right: 21px;
}

.rounded-circle {
    border-radius: 50%;
}

.cc-bcc-section {
    display: flex;
}

.cc-input {
    width: 351px !important;
    margin-right: 29px !important;
}

.bcc-input {
    width: 351px !important;
}

.from-to-section :deep(.el-input__wrapper) {
    background-color: #b7b7b7;
}

:deep(.el-input__wrapper) {
    margin-bottom: unset !important;
}

:deep(.ql-editor) {
    background-color: #fff !important;
}
</style>
